import type { IFocalPoint } from '@/interfaces/IStorefrontStyle';

export const FOCAL_STEP = 5;
/** The storefront phone cover (DESIGN.md section 3 R0): 390 px wide, banner aspect clamped to 128–168 px tall. */
export const PHONE_COVER_WIDTH = 390;
export const PHONE_COVER_MIN_HEIGHT = 128;
export const PHONE_COVER_MAX_HEIGHT = 168;
/**
 * The storefront phone cover's logo plate (Storefront V2 errata E110): 72 px, inside the banner's bottom-left corner,
 * on the 16 px page gutter and 12 px above the banner's bottom edge.
 */
export const PHONE_COVER_PLATE = { size: 72, left: 16, bottom: 12 } as const;

const ARROWS: Readonly<Record<string, readonly [number, number]>> = {
  ArrowLeft: [-1, 0],
  ArrowRight: [1, 0],
  ArrowUp: [0, -1],
  ArrowDown: [0, 1],
};

export interface IRectLike {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** Integer 0–100; anything not finite reads as the centre. */
export function clampFocal(value: number): number {
  if (!Number.isFinite(value)) return 50;
  return Math.min(100, Math.max(0, Math.round(value)));
}

export function focalFromPointer(
  rect: IRectLike,
  clientX: number,
  clientY: number
): IFocalPoint {
  if (!(rect.width > 0) || !(rect.height > 0)) return { x: 50, y: 50 };
  return {
    x: clampFocal(((clientX - rect.left) / rect.width) * 100),
    y: clampFocal(((clientY - rect.top) / rect.height) * 100),
  };
}

/** Arrow keys move 5 %; any other key → null. */
export function nudgeFocal(
  focal: IFocalPoint,
  key: string
): IFocalPoint | null {
  const step = ARROWS[key];
  if (!step) return null;
  return {
    x: clampFocal(focal.x + step[0] * FOCAL_STEP),
    y: clampFocal(focal.y + step[1] * FOCAL_STEP),
  };
}

/** The phone cover height for this image (the storefront's base rule, before its long-name and notice adjustments). */
export function phoneBannerHeight(
  imageWidth: number,
  imageHeight: number
): number {
  if (!(imageWidth > 0) || !(imageHeight > 0)) return PHONE_COVER_MAX_HEIGHT;
  const natural = Math.round((PHONE_COVER_WIDTH * imageHeight) / imageWidth);
  return Math.min(
    PHONE_COVER_MAX_HEIGHT,
    Math.max(PHONE_COVER_MIN_HEIGHT, natural)
  );
}

/** The part of the image a phone cover shows (`object-fit: cover` + `object-position: x% y%`), in % of the image. */
export interface IPercentRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

export function phoneCropRect(
  imageWidth: number,
  imageHeight: number,
  focal: IFocalPoint
): IPercentRect | null {
  if (!(imageWidth > 0) || !(imageHeight > 0)) return null;
  const boxHeight = phoneBannerHeight(imageWidth, imageHeight);
  const scale = Math.max(
    PHONE_COVER_WIDTH / imageWidth,
    boxHeight / imageHeight
  );
  const visibleWidth = Math.min(1, PHONE_COVER_WIDTH / (imageWidth * scale));
  const visibleHeight = Math.min(1, boxHeight / (imageHeight * scale));
  return {
    left: (1 - visibleWidth) * focal.x,
    top: (1 - visibleHeight) * focal.y,
    width: visibleWidth * 100,
    height: visibleHeight * 100,
  };
}

/**
 * Where the logo plate covers the photo, in % of the image like `phoneCropRect`: the plate's place in the phone
 * cover (left to right, the base height), carried into the part of the image the cover shows.
 */
export function phonePlateRect(
  imageWidth: number,
  imageHeight: number,
  focal: IFocalPoint
): IPercentRect | null {
  const crop = phoneCropRect(imageWidth, imageHeight, focal);
  if (!crop) return null;
  const boxHeight = phoneBannerHeight(imageWidth, imageHeight);
  const { size, left, bottom } = PHONE_COVER_PLATE;
  return {
    left: crop.left + (crop.width * left) / PHONE_COVER_WIDTH,
    top: crop.top + (crop.height * (boxHeight - bottom - size)) / boxHeight,
    width: (crop.width * size) / PHONE_COVER_WIDTH,
    height: (crop.height * size) / boxHeight,
  };
}
